import ErrorHandler from '../utils/ErrorHandler';
import { queryClient, supabase, asyncStoragePersister } from '../data/client';
import { ensureFreshSession } from '../utils/authRefresh';
import * as Sentry from '@sentry/react-native';
import { router } from 'expo-router';
import Toast from 'react-native-toast-message';

// Org entry is allowed only for these Stripe subscription statuses.
// null/missing/other statuses → blocked (fail closed).
// NOTE: legacy fallback only — `softwarePlan` on the org row and the
// get_organization_entitlements RPC are the authoritative gate now.
const ALLOWED_BILLING_STATUSES = ['active', 'trialing', 'past_due'];

const isBillingAllowed = (billingStatus) => {
    return ALLOWED_BILLING_STATUSES.includes(billingStatus);
}

/**
 * Fetches the caller's safe plan/entitlement state for the JWT organization.
 * The RPC derives the org from auth claims and returns { member: false } when
 * there is no org/membership — it never accepts a client-supplied org id.
 * Throws on transport/RPC failure so callers can fail closed WITHOUT touching
 * persisted org membership (a blip is not a plan change).
 */
const getOrganizationEntitlements = async () => {
    // The RPC revokes EXECUTE from `anon` — calling it without a session
    // (pre-auth mounts, focus/reconnect refetches after sign-out) returns
    // 42501 permission denied. No session is definitionally member:false.
    const {data: sessionData} = await supabase.auth.getSession();
    if(!sessionData?.session?.user) return {member: false};

    const {data, error} = await supabase.rpc('get_organization_entitlements');

    if(error){
        // Sign-out mid-flight flips the role to anon → 42501. Not a real error.
        const {data: post} = await supabase.auth.getSession();
        if(!post?.session?.user) return {member: false};
        ErrorHandler(Error(error.message), {query: "getOrganizationEntitlements", error});
        throw Error(error.message);
    }

    return data;
}

const getOrganizationByHandle = async (handle) => {
    //Anon Request - No Auth Required
    const {error, data} = await supabase.from('Organizations').select('*').eq('handle', handle).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getOrganizationByHandle", handle, error}, `Something went wrong fetching the organization`)
        return null;
    }


    return data || {};
}

const getOrganization = async (id) => {
    const currentOrgID = await getCurrentOrgID();
    const targetID = id ?? currentOrgID;
    if (!targetID) return null;
    const {error, data} = await supabase.from('Organizations').select('*').eq('id', targetID).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getOrganization", id, error}, `Something went wrong fetching the organization`)
        return null;
    }

    return data;
}

const getUsersOrganizations = async (userID) => {
    if(!userID) return [];

    const {error, data} = await supabase.from('RegisteredUsersOrganizations').select('*, Organization:Organizations(*)').eq('userID', userID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getUsersOrganizations", userID, error}, `Something went wrong fetching the organizations`)
        return null;
    }

    return data;
}

let _cachedOrgID = null;
let _cachedOrgTimezone = null;

// Cooldown to prevent redirect loops when orgID is persistently missing.
const REDIRECT_COOLDOWN_MS = 5000;
let _lastRedirectAt = 0;
let _isOrganizationSelectionActive = false;

const setOrganizationSelectionActive = (active) => {
    _isOrganizationSelectionActive = active;
}

const getCurrentOrgID = async () => {
    if(_cachedOrgID) return _cachedOrgID;

    const {data} = await supabase.auth.getSession();
    const session = data?.session;

    // No session at all — not logged in. Don't redirect; the layout auth
    // guard handles routing to login. Returning null lets callers bail.
    if(!session?.user){
        return null;
    }

    const storedOrgID = session?.user?.app_metadata?.organizationID;

    if(storedOrgID){
        _cachedOrgID = storedOrgID;
        return storedOrgID;
    }

    // Session exists but no orgID in app_metadata. The orgID claim may not
    // have been minted into the JWT yet (e.g. user just selected an org via
    // log_into_org but the local JWT is stale). Try refreshing once.
    //
    // Only refresh when the JWT has plenty of life left (>5 min). Near
    // expiry, autoRefreshToken will mint a new JWT (with the orgID) within
    // ~60s — calling refreshSession concurrently races on the same refresh
    // token and Supabase's reuse detection revokes the session family.
    const secondsLeft = session?.expires_at ? session.expires_at - Date.now() / 1000 : 0;
    if(secondsLeft > 300){
        try {
            await ensureFreshSession();
        } catch(e) {
            // Lock error, network error, etc. — don't crash, just continue.
            // The next call to getCurrentOrgID may succeed after
            // autoRefreshToken mints a fresh JWT.
            Sentry.logger.warn('refreshSession failed in getCurrentOrgID', { error: e?.message });
        }

        const {data: refreshed} = await supabase.auth.getSession();
        const refreshedOrgID = refreshed?.session?.user?.app_metadata?.organizationID;

        if(refreshedOrgID){
            _cachedOrgID = refreshedOrgID;
            return refreshedOrgID;
        }
    }

    // Still no orgID after refresh (or skipped refresh). User is logged in
    // but has no org selected — redirect to org selection. The cooldown
    // prevents redirect loops during transient states.
    const now = Date.now();
    if(now - _lastRedirectAt > REDIRECT_COOLDOWN_MS){
        _lastRedirectAt = now;
        Sentry.logger.warn("No organization ID found after session refresh — redirecting to org selection", { userID: session?.user?.id });
        if(!_isOrganizationSelectionActive){
            Toast.show({type: 'info', text2: 'Please select an organization to continue.', visibilityTime: 3000});
            router.replace('/auth/select-organization');
        }
    }

    return null;
}

// Synchronous read of the last-resolved org id (set by getCurrentOrgID).
// Used to key the entitlements query per organization without an async hop.
const getCachedOrgID = () => _cachedOrgID;

const clearOrgIDCache = () => {
    _cachedOrgID = null;
    _cachedOrgTimezone = null;
}

const getOrgTimezone = async () => {
    if (_cachedOrgTimezone) return _cachedOrgTimezone;

    const orgID = await getCurrentOrgID();
    if (!orgID) return 'UTC';

    const {data} = await supabase.from('Organizations').select('timezone').eq('id', orgID).maybeSingle();
    _cachedOrgTimezone = data?.timezone || 'UTC';
    return _cachedOrgTimezone;
}


const saveOrganization = async (details) => {
    const orgID = await getCurrentOrgID();

    const org = {
        name: details.name,
        timezone: details.timezone,
        logoPublicUrl: details.logoPublicUrl,
        logoPublicUrlDark: details.logoPublicUrlDark,
        logoPublicUrlLong: details.logoPublicUrlLong,
        logoPublicUrlLongDark: details.logoPublicUrlLongDark,
        weeklySummaryEnabled: details.weeklySummaryEnabled,
        weeklySummaryEmails: details.weeklySummaryEmails,
        enableSmsSending: details.enableSmsSending,
        enableGiving: details.enableGiving,
        givingEnabledAt: details.givingEnabledAt,
    }

    const {error} = await supabase.from('Organizations').update(org).eq('id', orgID);

    if(error){
        ErrorHandler(Error(error.message), {query: "saveOrganization", orgID, error}, `Something went wrong saving that logo`)
    }
}

const getActivity = async () => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('ActivityLog').select('*').eq('organization_id', orgID).order('timestamp', {ascending: false});

    if(error){
        ErrorHandler(Error(error.message), {query: "getActivity", orgID, error}, `Something went wrong fetching the activity log`)
        return [];
    }

    return data;
}


const globalSearch = async ({search_term, match_all, partial, types}) => {
    const trimmedSearchTerm = search_term.replace(/\s+/g, ' ').trim();

    const {data, error} = await supabase.rpc("universal_search", {search_term: trimmedSearchTerm, match_all, partial, types});

    if(error){
        ErrorHandler(Error(error.message), {query: "globalSearch", trimmedSearchTerm, match_all, partial, error}, `Something went wrong searching`)
        return [];
    }

    return data;
}


const logOutOfOrganization = async () => {
    const {error} = await supabase.rpc('log_out_of_org');
    
    if(error){
        ErrorHandler(Error(error.message), {mutation: "logOutOfOrganization", error}, `Something went wrong logging out of that organization`)
    }

    clearOrgIDCache();
    await ensureFreshSession();
    queryClient.clear();
    await asyncStoragePersister.removeClient();
}


const getImportTemplates = async () => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('ImportTemplates').select('*').eq('organizationID', orgID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getImportTemplates", orgID, error})
        return [];
    }

    return data;
}

const createImportTemplate = async ({name, mapping, type}) => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('ImportTemplates').insert({organizationID: orgID, name, mapping, type}).select();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createImportTemplate", input: {name, mapping, type}, error}, `Something went wrong creating the import template`)
        return null;
    }

    return data;
}

const getImportAssociations = async () => {
    const {error, data} = await supabase.rpc("get_import_associations")

    if(error){
        ErrorHandler(Error(error.message), {query: "getImportAssociations", error}, `Something went wrong fetching the import associations`)
        return [];
    }

    return data;
}


const upsertImportAssociations = async (associations) => {
    const orgID = await getCurrentOrgID();

    // Only persist confirmed associations — skip unconfirmed suggestions
    // (suggested: true) which are runtime-only and should not be saved.
    const confirmedAssociations = associations.filter((association) => association.suggested !== true);

    confirmedAssociations.forEach((association) => {
        delete association.associatedItem;
        delete association.suggested;
    });

    const newAssociations = confirmedAssociations.filter((association) => !association.id);
    if(newAssociations.length > 0){
        const {error: newAssociationsError} = await supabase.from('ImportAssociations').insert(newAssociations.map((association) => {
            // Strip id so the DB default (gen_random_uuid()) applies. An explicit
            // id: null in the insert payload violates the NOT NULL constraint even
            // though the column has a default. See CHURCH-APP-17F.
            const rest = { ...association, organizationID: orgID };
            delete rest.id;
            return rest;
        })).select();

        if (newAssociationsError) {
            ErrorHandler(Error(newAssociationsError?.message), {query: "importTransactions", input: {associations, error: newAssociationsError}})
            throw Error(newAssociationsError?.message);
        }
    }

    //Existing associations that may have changed
    const existingAssociations = queryClient.getQueryData(['importAssociations']) || [];

    for(const association of confirmedAssociations.filter((association) => association.id)){
        const existingAssociation = existingAssociations.find((existing) => existing.id === association.id);

        if(existingAssociation && existingAssociation.associatedID !== association.associatedID){
            const {error: updateAssociationError} = await supabase.from('ImportAssociations').update({associatedID: association.associatedID}).eq('id', association.id);

            if (updateAssociationError) {
                ErrorHandler(Error(updateAssociationError?.message), {query: "importTransactions", input: {associations, error: updateAssociationError}})
                throw Error(updateAssociationError?.message);
            }
        }
    }
}

const DEFAULT_QUICK_PERSON_FIELDS = ['email', 'phone', 'dateOfBirth'];

const getQuickPersonFields = async () => {
    const orgID = await getCurrentOrgID();
    
    const { data, error } = await supabase
        .from('Organizations')
        .select('quickPersonFields')
        .eq('id', orgID)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getQuickPersonFields", error });
        return DEFAULT_QUICK_PERSON_FIELDS;
    }

    return data?.quickPersonFields || DEFAULT_QUICK_PERSON_FIELDS;
};

const saveQuickPersonFields = async (fields) => {
    const orgID = await getCurrentOrgID();

    const { error } = await supabase
        .from('Organizations')
        .update({ quickPersonFields: fields })
        .eq('id', orgID)
        .select('quickPersonFields');

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "saveQuickPersonFields", input: fields, error }, "Something went wrong saving quick form fields");
        throw Error();
    }

    queryClient.invalidateQueries({ queryKey: ['quickPersonFields'] });
    return fields;
}

export {
    globalSearch,
    getOrganizationByHandle,
    getUsersOrganizations,
    getOrganization,
    getCurrentOrgID,
    clearOrgIDCache,
    getOrgTimezone,
    getActivity,
    saveOrganization,
    logOutOfOrganization,
    getImportTemplates,
    getImportAssociations,
    upsertImportAssociations,
    createImportTemplate,
    getQuickPersonFields,
    saveQuickPersonFields,
    DEFAULT_QUICK_PERSON_FIELDS,
    isBillingAllowed,
    ALLOWED_BILLING_STATUSES,
    getOrganizationEntitlements,
    getCachedOrgID,
    setOrganizationSelectionActive,
}